import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import axios from "axios";
import { useAuthContext } from "../hooks/useAuthContext";
import { useCompanyContext } from "../hooks/useCompanyContext";
import { useCultivationCompanyContext } from "../hooks/useCultivationCompanyContext";

const API = process.env.REACT_APP_API_URL;

const ensureArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

export default function NotificationSettingsSelect({ contacts = [] }) {
  const { user } = useAuthContext();
  const { company } = useCompanyContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveOk, setSaveOk] = useState(false);
  const [initialSettings, setInitialSettings] = useState(null);

  const { cultivationCompany } = useCultivationCompanyContext();

  const [settings, setSettings] = useState({
    order_confirmation: [],
    order_approval: [],
    document_approval: [],
    transport: [],
  });

  const isEqualSettings = (a, b) => {
    if (!a || !b) return false;

    const keys = ["order_confirmation", "order_approval", "document_approval", "transport"];

    return keys.every((k) => {
      const av = [...(a[k] || [])].sort();
      const bv = [...(b[k] || [])].sort();
      return JSON.stringify(av) === JSON.stringify(bv);
    });
  };

  const hasChanges = !isEqualSettings(settings, initialSettings);

  // refs
  const lastCompanyIdRef = useRef(null);
  const didInitApprovalRef = useRef(false);

  // state
  const [approvalEnabled, setApprovalEnabled] = useState(
    !!company?.approval_settings?.enabled
  );

  // 🔁 Synchroniseren bij wisselen van company
  useEffect(() => {
    const companyId = company?.Companyid;
    if (!companyId) return;

    if (lastCompanyIdRef.current !== companyId) {
      lastCompanyIdRef.current = companyId;
      didInitApprovalRef.current = false; // nieuwe company → geen autosave
      setApprovalEnabled(!!company?.approval_settings?.enabled);
    }
  }, [company?.Companyid]);

  // 💾 Autosave bij handmatige wijziging
  useEffect(() => {
    const companyId = company?.Companyid;
    if (!companyId || !user?.token) return;

    // eerste sync → NIET opslaan
    if (!didInitApprovalRef.current) {
      didInitApprovalRef.current = true;
      return;
    }

    const save = async () => {
      try {
        await axios.patch(
          `${API}/api/user/company/${companyId}/approval-settings`,
          { enabled: approvalEnabled },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      } catch (e) {
        console.error(
          "❌ approval-settings opslaan mislukt:",
          e?.response?.data || e.message
        );
        alert("Opslaan mislukt. Probeer opnieuw.");

        // revert bij fout
        setApprovalEnabled((prev) => !prev);
      }
    };

    save();
  }, [approvalEnabled]);

  

  // 1) Maak options uit contacts (alleen met email)
  const emailOptions = useMemo(() => {
    const emails = contacts
      .map((c) => (c?.E_Mail || "").trim())
      .filter(Boolean);

    // unique + sort
    const unique = Array.from(new Set(emails.map((e) => e.toLowerCase())));
    unique.sort((a, b) => a.localeCompare(b));

    return unique.map((email) => ({ value: email, label: email }));
  }, [contacts]);

  // 2) Notification settings ophalen zodra company beschikbaar is
  useEffect(() => {
    const companyId = company?.Companyid;
    if (!companyId || !user?.token) return;

    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${API}/api/user/company/${companyId}/notification-settings`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );

        const ns = res.data?.notification_settings || {};

        const normalized = {
        order_confirmation: ensureArray(ns.order_confirmation),
        order_approval: ensureArray(ns.order_approval),
        document_approval: ensureArray(ns.document_approval),
        invoice_confirmation: ensureArray(ns.invoice_confirmation),
        transport: ensureArray(ns.transport),
        };

        setSettings(normalized);
        setInitialSettings(normalized);
      } catch (e) {
        console.error("❌ Notification settings ophalen mislukt:", e?.response?.data || e.message);
        setError("Kon notification settings niet laden.");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [company?.Companyid, user?.token]);

  // 3) Helper: van array emails -> react-select value
  const toSelectValue = (emails) =>
    ensureArray(emails)
      .map((e) => (e || "").trim())
      .filter(Boolean)
      .map((e) => ({ value: e.toLowerCase(), label: e.toLowerCase() }));

  // 4) Handler: select change -> state updaten (nog niet opslaan)
  const onChange = (key, selected) => {
    const emails = (selected || []).map((o) => o.value);
    setSettings((prev) => ({ ...prev, [key]: emails }));
  };

  const saveSettings = async () => {
    const companyId = company?.Companyid;
    if (!companyId || !user?.token) return;

    setSaving(true);
    setSaveError(null);
    setSaveOk(false);

    try {
      await axios.patch(
        `${API}/api/user/company/${companyId}/notification-settings`,
        {
          notification_settings: settings,
        },
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      setSaveOk(true);
    } catch (e) {
      console.error("❌ Notification settings opslaan mislukt:", e?.response?.data || e.message);
      setSaveError("Opslaan mislukt. Probeer opnieuw.");
    } finally {
      setSaving(false);
      setInitialSettings(settings);
    }
  };

  const canSeeWPK = (cultivationCompany.name === 'WPK');
  const canSeePVG = (cultivationCompany.name === 'P. van Geest');

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ marginBottom: 12 }} className="pageheading">E-mail notificaties</h3>

      {loading && <p>⏳ Laden…</p>}
      {error && <p style={{ color: "crimson" }}>⚠️ {error}</p>}

      <div style={{ display: "grid", gap: 16, maxWidth: 520 }}>
        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
            Order bevestiging
          </label>
          <Select
            isMulti
            options={emailOptions}
            value={toSelectValue(settings.order_confirmation)}
            onChange={(v) => onChange("order_confirmation", v)}
            placeholder="Selecteer e-mail(s)…"
            noOptionsMessage={() => "Geen e-mails beschikbaar"}
          />
        </div>
        {canSeeWPK && (
        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
            Order goedkeuring
          </label>
          <Select
            isMulti
            options={emailOptions}
            value={toSelectValue(settings.order_approval)}
            onChange={(v) => onChange("order_approval", v)}
            placeholder="Selecteer e-mail(s)…"
            noOptionsMessage={() => "Geen e-mails beschikbaar"}
          />
        </div>
        )}
        {canSeeWPK && (
        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
            Document goedkeuring
          </label>
          <Select
            isMulti
            options={emailOptions}
            value={toSelectValue(settings.document_approval)}
            onChange={(v) => onChange("document_approval", v)}
            placeholder="Selecteer e-mail(s)…"
            noOptionsMessage={() => "Geen e-mails beschikbaar"}
          />
        </div>
        )}
        {canSeeWPK && (
        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
            Transport
          </label>
          <Select
            isMulti
            options={emailOptions}
            value={toSelectValue(settings.transport)}
            onChange={(v) => onChange("transport", v)}
            placeholder="Selecteer e-mail(s)…"
            noOptionsMessage={() => "Geen e-mails beschikbaar"}
          />
        </div>
        )}
        {canSeePVG && (
        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
            Facturatie
          </label>
          <Select
            isMulti
            options={emailOptions}
            value={toSelectValue(settings.invoice_confirmation)}
            onChange={(v) => onChange("invoice_confirmation", v)}
            placeholder="Selecteer e-mail(s)…"
            noOptionsMessage={() => "Geen e-mails beschikbaar"}
          />
        </div>
        )}
        {saveError && <p style={{ color: "crimson" }}>⚠️ {saveError}</p>}
        {saveOk && <p style={{ color: "green" }}>✔ Opgeslagen</p>}
        {canSeeWPK && (
        <button
        type="button"
        onClick={saveSettings}
        disabled={!hasChanges || loading}
        style={{
            alignSelf: "flex-start",
            padding: "10px 14px",
            borderRadius: 8,
            border: "1px solid #ccc",
            color: "#fff",
            background: saving ? "#eee" : "rgb(13, 110, 253)",
            cursor: saving ? "not-allowed" : "pointer",
            fontWeight: 600,
            opacity: !hasChanges ? 0.5 : 1,
            cursor: !hasChanges ? "not-allowed" : "pointer",
        }}
        >
        {saving ? "Opslaan…" : "Opslaan"}
        </button>
        )}

      </div>
      {Array.isArray(user?.roles) && user.roles.includes("actorius") && (
          <div style={{ marginTop: 16, padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700 }}>4-ogen principe voor order goedkeuring</div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                Als dit aan staat moeten orders door 2 personen worden goedgekeurd.
              </div>
            </div>
      
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={approvalEnabled}
              onChange={(e) => setApprovalEnabled(e.target.checked)}
            />
            <span>{approvalEnabled ? "Aan" : "Uit"}</span>

            </label>
          </div>
        </div>
      )}
    </div>
  );
}